import { invalidateFulfillmentQuotes } from './use-store-fulfillment';
import {
  useMutation,
  useQueryClient,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { SupabaseClient } from '../supabase/client';
import {
  createAdminPreparedOrder,
  searchAdminOrderSellableVariants,
} from '../services/order.service';
import type {
  AdminOrderSellableVariant,
  CreateAdminPreparedOrderInput,
  CreateAdminPreparedOrderResult,
} from '../types/order';

export const adminPreparedOrderKeys = {
  all: ['admin-prepared-order'] as const,
  sellables: (
    storeId: string,
    query: string,
    categoryId: string | null,
    limit: number,
    offset: number,
  ) => ['admin-prepared-order', 'sellables', storeId, query, categoryId, limit, offset] as const,
};

export interface UseAdminOrderSellableVariantsInput {
  readonly storeId: string | null;
  readonly query: string;
  readonly categoryId?: string | null;
  readonly limit?: number;
  readonly offset?: number;
}

function normalizeLimit(limit: number | undefined): number {
  if (typeof limit !== 'number' || !Number.isFinite(limit)) return 50;
  return Math.min(Math.max(Math.trunc(limit), 1), 100);
}

function normalizeOffset(offset: number | undefined): number {
  if (typeof offset !== 'number' || !Number.isFinite(offset)) return 0;
  return Math.max(Math.trunc(offset), 0);
}

export function useAdminOrderSellableVariants(
  client: SupabaseClient,
  input: UseAdminOrderSellableVariantsInput,
): UseQueryResult<AdminOrderSellableVariant[]> {
  const query = input.query.trim();
  const categoryId = input.categoryId ?? null;
  const limit = normalizeLimit(input.limit);
  const offset = normalizeOffset(input.offset);

  return useQuery<AdminOrderSellableVariant[]>({
    queryKey: input.storeId
      ? adminPreparedOrderKeys.sellables(input.storeId, query, categoryId, limit, offset)
      : adminPreparedOrderKeys.sellables('no-store', query, categoryId, limit, offset),
    enabled: Boolean(input.storeId),
    staleTime: 30_000,
    queryFn: () => {
      if (!input.storeId) return Promise.resolve([]);
      return searchAdminOrderSellableVariants(client, {
        storeId: input.storeId,
        query,
        categoryId,
        limit,
        offset,
      });
    },
  });
}

export function useCreateAdminPreparedOrder(
  client: SupabaseClient,
): UseMutationResult<CreateAdminPreparedOrderResult, unknown, CreateAdminPreparedOrderInput> {
  const queryClient = useQueryClient();
  return useMutation<CreateAdminPreparedOrderResult, unknown, CreateAdminPreparedOrderInput>({
    retry: false,
    mutationKey: ['create-admin-prepared-order'] as const,
    mutationFn: (input) => createAdminPreparedOrder(client, input),
    onSuccess: (_result, input) => invalidateFulfillmentQuotes(queryClient, input.storeId),
  });
}
